import { IconButton } from "@somethings-ui/web";
import { Plus, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { getActivityUnavailableTooltip } from "@/components/conversations/activities/use-available-activity-items";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { AvailableActivity } from "@/lib/api/activities";
import type { UseActionAnalyticsResult } from "./use-action-analytics";
import { useActionLaunch } from "./use-action-launch";
import { useActionStore } from "./use-action-store";
import { ACTIVITY_TOOL_ORDER, CONTENT_TOOLS } from "./tools/tool-config";
import { ActivityToolThumbnail, ContentToolThumbnail } from "./tools/tool-thumbnail";

interface ActionFloatingAddProps {
  analytics: UseActionAnalyticsResult;
}

export function ActionFloatingAdd({ analytics }: ActionFloatingAddProps) {
  const setTab = useActionStore((s) => s.setTab);
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    activities,
    selectActivity,
    canShareContent,
    contentGates,
    openContentCreate,
  } = useActionLaunch();

  const activitiesByType = new Map(activities.map((item) => [item.type, item]));

  const openActivity = (activity: AvailableActivity) => {
    analytics.trackToolSelect("activity", activity.type);
    selectActivity(activity);
    setMenuOpen(false);
  };

  const openToolkit = () => {
    analytics.trackTabSwitch("history", "tools");
    setTab("tools");
    setMenuOpen(false);
  };

  const visibleContent = CONTENT_TOOLS.filter((tool) => contentGates[tool.id]);

  return (
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger asChild>
        <IconButton
          variant="primary"
          size="lg"
          shape="circle"
          aria-label={menuOpen ? "Close add menu" : "Add"}
          aria-expanded={menuOpen}
          className="absolute bg-black active:bg-black bottom-4 right-4 z-10 shadow-[0px_2px_8px_rgba(0,0,0,0.16)]"
          icon={
            menuOpen ? (
              <X className="size-5" strokeWidth={2.25} />
            ) : (
              <Plus className="size-5" strokeWidth={2.25} />
            )
          }
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        sideOffset={12}
        className="w-[220px] rounded-[24px] border border-surfaces-divider bg-white p-2 shadow-[0px_8px_24px_rgba(0,0,0,0.12)]"
      >
        <div className="flex flex-col">
          {ACTIVITY_TOOL_ORDER.map((id) => {
            const activity = activitiesByType.get(id);
            if (!activity) return null;
            const disabled = !activity.enabled || !activity.isTeenEligible;
            const tooltip = getActivityUnavailableTooltip(activity);
            return (
              <QuickActionRow
                key={id}
                thumbnail={<ActivityToolThumbnail id={activity.type} />}
                label={activity.label}
                disabled={disabled}
                title={disabled ? tooltip || undefined : undefined}
                onClick={() => {
                  if (disabled) return;
                  openActivity(activity);
                }}
              />
            );
          })}

          {canShareContent
            ? visibleContent.map((tool) => (
                <QuickActionRow
                  key={tool.id}
                  thumbnail={<ContentToolThumbnail id={tool.id} />}
                  label={tool.shortLabel}
                  onClick={() => {
                    openContentCreate(tool.id);
                    setMenuOpen(false);
                  }}
                />
              ))
            : null}

          <button
            type="button"
            onClick={openToolkit}
            className="mt-1 w-full py-2 text-center font-body text-body-3 text-text-tertiary transition-colors hover:text-text-secondary"
          >
            Open toolkit
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function QuickActionRow({
  thumbnail,
  label,
  disabled,
  title,
  onClick,
}: {
  thumbnail: ReactNode;
  label: string;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-2xl px-2 py-1.5 text-left transition-colors hover:bg-surfaces-z0 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {thumbnail}
      <span className="font-body text-body-2 text-text-primary">{label}</span>
    </button>
  );
}
